//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import Icon from '@components/atoms/icon/Icon';
import { BodyText, LinkPrimaryText } from '@components/atoms/text/Text';
import {
  FacebookIcon,
  InstagramIcon,
  PinterestIcon,
  TikTokIcon,
  TwitterIcon,
  YouTubeIcon,
} from '@components/molecules/SocialNetworkIcon';
import { SocialNetwork } from '@models/SocialNetwork';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { isDefined } from '@utils/filterPredicates';

const ICON_SIZE = 16;

const networksConfig = [
  {
    network: SocialNetwork.Pinterest,
    icon: <PinterestIcon size={ICON_SIZE} />,
    label: 'View on Pinterest',
  },
  {
    network: SocialNetwork.Instagram,
    icon: <InstagramIcon size={ICON_SIZE} />,
    label: 'View on Instagram',
  },
  {
    network: SocialNetwork.Tumblr,
    label: 'View on Tumblr',
  },
  {
    network: SocialNetwork.Facebook,
    icon: <FacebookIcon size={ICON_SIZE} />,
    label: 'View on Facebook',
  },
  {
    network: SocialNetwork.Twitter,
    icon: <TwitterIcon size={ICON_SIZE} />,
    label: 'View on Twitter',
  },
  {
    network: SocialNetwork.TikTok,
    icon: <TikTokIcon size={ICON_SIZE} />,
    label: 'View on TikTok',
  },
  {
    network: SocialNetwork.YouTube,
    icon: <YouTubeIcon size={ICON_SIZE} />,
    label: 'View on YouTube',
  },
];

export interface UserWebLinkProps {
  href?: string;
  isProfile?: boolean;
  network: SocialNetwork;
}

export const UserWebLink = ({ href, network }: UserWebLinkProps) => {
  const config = networksConfig.find((config) => config.network === network);

  return isDefined(config) ? (
    <PrimaryAnchor
      href={href}
      rel='noreferrer'
      target='_blank'
      trackingId={UIATrackingIds.DETAILS_OPEN_USER_WEB_LINK}
    >
      <Label>
        <LinkPrimaryText>{config.label}</LinkPrimaryText>
        {config.icon && <>{config.icon}</>}
      </Label>
    </PrimaryAnchor>
  ) : (
    <Label>
      <BodyText>Uploaded</BodyText>
      <Icon name='upload' />
    </Label>
  );
};

const Label = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spacings[2]}
  `,
);
